import './styles.scss';
import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Paper,
    Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Header, Input, Spinner } from '../../components';
import { stripNonNumbers } from '../../helpers';
import config from '../../config';

const PAGE_STATE = Object.freeze({
    VALIDATING_TOKEN: 'VALIDATING_TOKEN',
    ENTER_CODE: 'ENTER_CODE',
    OPENING_REPORT: 'OPENING_REPORT',
    SHOW_REPORT: 'SHOW_REPORT',
});
const MAX_INVALID_ACCESS_CODE_ATTEMPTS = 3;
const REPORT_SESSION_DURATION_MS = 10 * 60 * 1000;
const REPORT_SESSION_WARNING_MS = 60 * 1000;

function HealthReportViewer() {
    const { id: shareIdParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [shareId, setShareId] = useState('');
    const [shareToken, setShareToken] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [reportUrl, setReportUrl] = useState('');
    const [pageState, setPageState] = useState(PAGE_STATE.VALIDATING_TOKEN);
    const [errorMessage, setErrorMessage] = useState('');
    const [invalidAttempts, setInvalidAttempts] = useState(0);
    const [showSessionEndingWarning, setShowSessionEndingWarning] = useState(false);
    const accessCodeInputRef = useRef(null);

    useEffect(() => () => {
        if (reportUrl) {
            window.URL.revokeObjectURL(reportUrl);
        }
    }, [reportUrl]);

    useEffect(() => {
        if (pageState === PAGE_STATE.ENTER_CODE) {
            accessCodeInputRef.current?.focus();
        }
    }, [pageState]);

    useEffect(() => {
        if (pageState !== PAGE_STATE.SHOW_REPORT || !reportUrl) {
            setShowSessionEndingWarning(false);
            return undefined;
        }

        setShowSessionEndingWarning(false);

        const warningTimeout = window.setTimeout(() => {
            setShowSessionEndingWarning(true);
        }, REPORT_SESSION_DURATION_MS - REPORT_SESSION_WARNING_MS);

        const redirectTimeout = window.setTimeout(() => {
            navigate('/login');
        }, REPORT_SESSION_DURATION_MS);

        return () => {
            window.clearTimeout(warningTimeout);
            window.clearTimeout(redirectTimeout);
        };
    }, [navigate, pageState, reportUrl]);

    const clearReport = () => {
        if (reportUrl) {
            window.URL.revokeObjectURL(reportUrl);
        }
        setReportUrl('');
    };

    const goToLogin = () => {
        navigate('/login');
    };

    const validateToken = async (id, token) => {
        setPageState(PAGE_STATE.VALIDATING_TOKEN);
        setErrorMessage('');
        clearReport();

        try {
            const response = await fetch(
                `${config.apiUrl}/case-api/health-report-shares/v1/${id}/access`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                },
            );

            if (!response.ok) {
                throw new Error('Unable to validate share ID and token combination');
            }

            setAccessCode('');
            setPageState(PAGE_STATE.ENTER_CODE);
        } catch (err) {
            console.error('An error occurred while requesting access; redirecting to login:', err);
            goToLogin();
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const id = shareIdParam;
        const token = searchParams.get('token');

        clearReport();
        setShareId('');
        setShareToken('');
        setAccessCode('');
        setErrorMessage('');
        setInvalidAttempts(0);
        setPageState(PAGE_STATE.VALIDATING_TOKEN);

        if (!id || !token) {
            goToLogin();
            return;
        }

        setShareId(id);
        setShareToken(token);
        validateToken(id, token);
    }, [location.key, location.search, shareIdParam]);

    const renderValidatingTokenState = () => (
        <div className="health-report-viewer-state">
            <Spinner type="center" />
        </div>
    );

    const verifyAccess = async (event) => {
        event.preventDefault();

        if (!accessCode.trim() || accessCode.trim().length !== 6) {
            setErrorMessage('Enter a valid 6-digit access code.');
            return;
        }

        setPageState(PAGE_STATE.OPENING_REPORT);
        setErrorMessage('');
        clearReport();

        try {
            const verifyResponse = await fetch(
                `${config.apiUrl}/case-api/health-report-shares/v1/${shareId}/access/verify`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code: accessCode.trim() }),
                },
            );

            if (verifyResponse.status === 403) {
                const nextInvalidAttempts = invalidAttempts + 1;

                setInvalidAttempts(nextInvalidAttempts);

                if (nextInvalidAttempts >= MAX_INVALID_ACCESS_CODE_ATTEMPTS) {
                    goToLogin();
                    return;
                }

                setErrorMessage('That access code is invalid or has expired. Please check the code and try again.');
                setPageState(PAGE_STATE.ENTER_CODE);
                setAccessCode('');
                return;
            }

            if (!verifyResponse.ok) {
                throw new Error('Unexpected error validating access code - non-OK response');
            }

            const { accessToken } = await verifyResponse.json();

            const reportResponse = await fetch(
                `${config.apiUrl}/case-api/health-report-shares/v1/${shareId}/report`,
                {
                    headers: { 'X-Ori-Health-Report-Authorization-Token': `Bearer ${accessToken}` },
                },
            );

            if (!reportResponse.ok) {
                throw new Error('Unable to access report');
            }

            const reportBlob = await reportResponse.blob();
            const nextReportUrl = window.URL.createObjectURL(reportBlob);
            setInvalidAttempts(0);
            setReportUrl(nextReportUrl);
            setPageState(PAGE_STATE.SHOW_REPORT);
        } catch (err) {
            console.error('An error occurred while verifying access code:', err);
            goToLogin();
        }
    };

    const resendCode = () => {
        setInvalidAttempts(0);
        validateToken(shareId, shareToken);
    };

    const isOpenReportDisabled = accessCode.trim().length !== 6;

    const renderEnterCodeState = () => (
        <div className="health-report-viewer-state">
            <Typography variant="h5">
                Enter Access Code
            </Typography>
            <Typography variant="body1" className="health-report-viewer-subtitle">
                Please enter the 6-digit access code sent to your email.
            </Typography>
            {errorMessage && (
                <Alert severity="error">
                    {errorMessage}
                </Alert>
            )}
            <form className="health-report-viewer-form" onSubmit={verifyAccess}>
                <Input
                    label="Access Code"
                    variant="outlined"
                    placeholder="Access Code"
                    value={accessCode}
                    inputRef={accessCodeInputRef}
                    inputProps={{
                        maxLength: 6,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    onChange={(event) => setAccessCode(stripNonNumbers(event.target.value))}
                />
                <div className="health-report-viewer-actions">
                    <Button type="submit" variant="contained" className="health-report-viewer-button-primary" disabled={isOpenReportDisabled}>
                        Open Report
                    </Button>
                    <Button type="button" variant="outlined" className="health-report-viewer-button-secondary" onClick={resendCode}>
                        Resend Code
                    </Button>
                </div>
            </form>
        </div>
    );

    const renderOpeningReportState = () => (
        <div className="health-report-viewer-state">
            <Spinner type="center" />
        </div>
    );

    const renderReportState = () => (
        <div className="health-report-viewer-report">
            <div className="health-report-viewer-toolbar">
                <Button
                    variant="outlined"
                    className="health-report-viewer-button-secondary"
                    href={reportUrl}
                    download="health-report.pdf"
                >
                    Download PDF
                </Button>
                {showSessionEndingWarning && (
                    <Typography variant="body2" className="health-report-viewer-token-note">
                        Your session will end in one minute
                    </Typography>
                )}
            </div>
            <iframe
                title="Health report PDF"
                src={reportUrl}
                className="health-report-viewer-frame"
            />
        </div>
    );

    const renderPageBody = () => {
        switch (pageState) {
            case PAGE_STATE.VALIDATING_TOKEN:
                return renderValidatingTokenState();
            case PAGE_STATE.ENTER_CODE:
                return renderEnterCodeState();
            case PAGE_STATE.OPENING_REPORT:
                return renderOpeningReportState();
            case PAGE_STATE.SHOW_REPORT:
                return renderReportState();
            default:
                return null;
        }
    };

    return (
        <>
            <Header label="Health Report Viewer" showMenu={false} />
            <main className="health-report-viewer-container">
                <Paper className="health-report-viewer-card" elevation={0}>
                    {renderPageBody()}
                </Paper>
            </main>
        </>
    );
}

export default HealthReportViewer;
